import React from 'react';

// Color / size / option picker for one quote-cart line. The choice is stored on the line as a
// single label (SharedQuoteItem.selectedVariantLabel), e.g. "Red / XL", which is what the
// quote and its PDF show. Options come from the product's variants (color/size combinations,
// or label-only options); a product without variants falls back to its plain
// comma-separated `colors` / `sizes` lists.

const splitList = (s) => [...new Set((s || '').split(',').map(x => x.trim()).filter(Boolean))];
const unique = (arr) => [...new Set(arr.filter(Boolean))];

// Trimmed, matching the backend's QuoteController#variantLabelOf.
export const variantLabelOf = (v) =>
    (v.label || '').trim() || [v.color, v.size].map(s => (s || '').trim()).filter(Boolean).join(' / ');

export const hasVariantOptions = (item) =>
    (item?.variants?.length > 0) || splitList(item?.colors).length > 0 || splitList(item?.sizes).length > 0;

const selectStyle = {
    padding: '3px 6px', fontSize: '0.8rem', borderRadius: '4px', border: '1px solid #ccc',
    color: '#333', backgroundColor: '#fff', maxWidth: '150px',
};

const Select = ({ label, value, options, onChange, disabled }) => (
    <select
        aria-label={label}
        style={{ ...selectStyle, borderColor: value ? '#ccc' : '#f0a868' }}
        value={value || ''}
        onChange={e => onChange(e.target.value || null)}
        disabled={disabled}
    >
        <option value="">{label}…</option>
        {/* Keep a saved value that is no longer offered (option removed later) selectable. */}
        {value && !options.includes(value) && <option value={value}>{value}</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
);

const QuoteVariantPicker = ({ item, value, onChange, disabled = false }) => {
    if (!hasVariantOptions(item)) return null;

    // Unavailable variants aren't offered — except the one this line already has, which stays
    // selectable so a saved choice is never silently shown as blank.
    const variants = (item.variants || []).filter(v => v.available !== false || (value && variantLabelOf(v) === value));
    const withAxes = variants.filter(v => v.color || v.size);
    const parts = (value || '').split(' / ').map(s => s.trim());
    let reflected = !value; // does a dropdown below show the saved value?
    const missing = [];     // what still needs picking on this line — drives the helper text

    let body;
    if (withAxes.length > 0) {
        // Color × size combinations — size options depend on the chosen color.
        const current = withAxes.find(v => variantLabelOf(v) === value);
        const colors = unique(withAxes.map(v => v.color));
        const color = current ? current.color : (colors.includes(parts[0]) ? parts[0] : null);
        const sizesFor = (c) => unique(withAxes.filter(v => !colors.length || v.color === c).map(v => v.size));
        const sizes = sizesFor(color);
        const size = current ? current.size : (sizes.includes(parts[parts.length - 1]) ? parts[parts.length - 1] : null);
        reflected = reflected || !!current;
        if (colors.length > 0 && !color) missing.push('color');
        // A size is needed when the chosen color comes in sizes (or, before a color is picked,
        // when any variant has one).
        if (!size && (color || !colors.length ? sizes.length > 0 : withAxes.some(v => v.size))) missing.push('size');

        const pick = (c, s) => {
            const match = withAxes.find(v => (v.color || null) === (c || null) && (v.size || null) === (s || null));
            onChange(match ? variantLabelOf(match) : ([c, s].filter(Boolean).join(' / ') || null));
        };

        body = (
            <>
                {colors.length > 0 && (
                    <Select label="Color" value={color} options={colors} disabled={disabled}
                        onChange={c => pick(c, sizesFor(c).includes(size) ? size : (sizesFor(c).length === 1 ? sizesFor(c)[0] : null))} />
                )}
                {sizes.length > 0 && (
                    <Select label="Size" value={size} options={sizes} disabled={disabled || (colors.length > 0 && !color)}
                        onChange={s => pick(color, s)} />
                )}
            </>
        );
    } else if (variants.length > 0) {
        // Label-only options (e.g. "64 GB", "Pack of 6"). Select keeps an unlisted saved value.
        reflected = true;
        if (!value) missing.push('option');
        body = <Select label="Option" value={value} options={unique(variants.map(variantLabelOf))} disabled={disabled} onChange={onChange} />;
    } else {
        // No variants — the product's plain color / size lists.
        const colors = splitList(item.colors);
        const sizes = splitList(item.sizes);
        const color = colors.includes(parts[0]) ? parts[0] : null;
        const size = sizes.includes(parts[parts.length - 1]) ? parts[parts.length - 1] : null;
        reflected = reflected || value === [color, size].filter(Boolean).join(' / ');
        if (colors.length > 0 && !color) missing.push('color');
        if (sizes.length > 0 && !size) missing.push('size');
        const pick = (c, s) => onChange([c, s].filter(Boolean).join(' / ') || null);
        body = (
            <>
                {colors.length > 0 && <Select label="Color" value={color} options={colors} disabled={disabled} onChange={c => pick(c, size)} />}
                {sizes.length > 0 && <Select label="Size" value={size} options={sizes} disabled={disabled} onChange={s => pick(color, s)} />}
            </>
        );
    }

    return (
        <div className="qcp-variant-picker" style={{ marginTop: '4px' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {body}
                {!reflected && (
                    <span style={{ fontSize: '0.78rem', color: '#b06000', alignSelf: 'center' }}
                        title="Saved on this line, but no longer one of the product's options. Pick a new one to replace it.">
                        Saved: {value}
                    </span>
                )}
            </div>
            {/* A reminder only — it never blocks saving or creating the quote. */}
            {missing.length > 0 && reflected && (
                <div role="status" style={{ fontSize: '0.78rem', color: '#b06000', marginTop: '3px' }}>
                    ⚠ Please select {missing.join(' and ')} for this item
                </div>
            )}
        </div>
    );
};

export default QuoteVariantPicker;
